import {
  getCurrencyDrawerOptions,
  type ColumnDef,
  type Currency,
  type Customer,
  type InputsAndDropdownsDrawerOption,
  type ISchedule,
  type MarketAccount,
  type ToggleGroupOption,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { getMarginTradingOption } from './mappers';
import { MarginTradingIndicatorCell } from './MarginTradingIndicatorCell';
import type {
  CustomerMarginConfiguration,
  CustomerMarginConfigurationDrawerEntity,
  MarginTradingOption,
} from './types';

const MARGIN_TRADING_OPTIONS = [
  { displayName: 'Enabled', value: 'enabled' },
  { displayName: 'Disabled', value: 'disabled' },
] as const satisfies readonly ToggleGroupOption<MarginTradingOption>[];

const MARGIN_TRADING_SORT_ORDER: readonly (MarginTradingOption | undefined)[] = [undefined, 'disabled', 'enabled'];

export const CUSTOMER_MARGIN_CONFIGURATIONS_COLUMNS: ColumnDef<CustomerMarginConfiguration>[] = [
  {
    type: 'custom',
    id: 'MarginTradingIndicator',
    title: '',
    initialWidth: 44,
    minWidth: 44,
    maxWidth: 44,
    sortable: true,
    suppressHeaderMenuButton: true,
    params: {
      suppressColumnsToolPanel: true,
      headerClass: 'ag-center-aligned-cell',
      cellClass: 'ag-center-aligned-cell',
      valueGetter: ({ data }: ValueGetterParams<CustomerMarginConfiguration>) =>
        getMarginTradingOption(data?.MarginTrading),
      comparator: (a, b) => MARGIN_TRADING_SORT_ORDER.indexOf(a) - MARGIN_TRADING_SORT_ORDER.indexOf(b),
      cellRenderer: ({ value }: ICellRendererParams<CustomerMarginConfiguration, MarginTradingOption>) => (
        <MarginTradingIndicatorCell value={value} />
      ),
    },
  },
  {
    type: 'marketAccount',
    field: 'MarketAccount',
    title: 'Customer Account',
    params: {
      prefixCustomerMarketAccountsWithCpty: true,
    },
  },
  {
    type: 'number',
    field: 'Leverage',
    title: 'Leverage',
    initialWidth: 90,
    params: {
      currency: 'x',
    },
  },
  {
    type: 'text',
    field: 'MarginTrading',
    title: 'Margin Trading',
    initialWidth: 130,
    params: {
      getLabel: value => {
        const option = getMarginTradingOption(value);
        return option === 'enabled' ? 'Enabled' : option === 'disabled' ? 'Disabled' : '';
      },
    },
  },
  {
    type: 'percent',
    field: 'MarginStressFactor',
    title: 'Margin Stress Factor',
    initialWidth: 160,
  },
  {
    type: 'text',
    field: 'FundingSchedule',
    title: 'Funding Schedule',
  },
  {
    type: 'percent',
    field: 'MarginCallThreshold',
    title: 'Margin Call Threshold',
    initialWidth: 170,
  },
  {
    type: 'percent',
    field: 'LiquidationThreshold',
    title: 'Liquidation Threshold',
    initialWidth: 170,
  },
  {
    type: 'number',
    field: 'MaximumExposure',
    title: 'Maximum Exposure',
    params: {
      currencyField: 'MaximumExposureCurrency',
    },
  },
];

export function getCustomerAccountConfigurationDrawerOptions({
  customerMarketAccountsList,
  configuredMarketAccountNames,
  customers,
  currencies,
  schedules,
}: {
  customerMarketAccountsList: MarketAccount[];
  configuredMarketAccountNames: ReadonlySet<string>;
  customers: Customer[];
  currencies: Pick<Currency, 'Symbol' | 'Description'>[];
  schedules: ISchedule[];
}): InputsAndDropdownsDrawerOption<CustomerMarginConfigurationDrawerEntity>[] {
  const customersByName = new Map(customers.map(c => [c.Name, c]));

  return [
    {
      field: 'MarketAccount',
      type: 'dropdown',
      getDropdownOptions: (_, isEditing) => {
        // Editing allows all market accounts; only create mode filters out already-configured ones.
        const filteredMarketAccounts = customerMarketAccountsList.filter(
          ({ Name }) => isEditing || !configuredMarketAccountNames.has(Name)
        );

        return filteredMarketAccounts.map(({ Name, SourceAccountID, Counterparty }) => {
          const customer = customersByName.get(Counterparty);
          const customerDisplayName = customer?.DisplayName ?? Counterparty;
          const accountID = SourceAccountID ?? Name;
          return {
            value: Name,
            label: `${customerDisplayName} - ${accountID}`,
          };
        });
      },
      title: 'Customer Account',
      getIsRequired: () => true,
      disabledWhenEditing: true,
    },
    {
      field: 'MarginTrading',
      type: 'toggle',
      title: 'Margin Trading',
      getToggleOptions: () => [...MARGIN_TRADING_OPTIONS],
      getIsRequired: () => true,
    },
    {
      field: 'Leverage',
      type: 'inputNumeric',
      numberInputProps: { integerOnly: true },
      title: 'Leverage',
      getInputSuffix: () => 'x',
      getIsRequired: () => true,
    },
    {
      field: 'MarginStressFactor',
      type: 'inputNumeric',
      variant: 'percent',
      title: 'Margin Stress Factor',
    },
    {
      field: 'FundingSchedule',
      type: 'dropdown',
      getDropdownOptions: () =>
        schedules.map(({ Interval, Name, StartTime, TimeZone }) => ({
          value: Name,
          label: Name,
          description: `Start: ${StartTime} • TZ: ${TimeZone} • Interval: ${Interval}`,
        })),
      title: 'Funding Schedule',
      getIsRequired: () => true,
      placeholder: 'Select funding schedule',
    },
    {
      field: 'MarginCallThreshold',
      type: 'inputNumeric',
      variant: 'percent',
      title: 'Margin Call Threshold',
    },
    {
      field: 'LiquidationThreshold',
      type: 'inputNumeric',
      variant: 'percent',
      title: 'Liquidation Threshold',
    },
    {
      field: 'MaximumExposureCurrency',
      type: 'dropdown',
      title: 'Maximum Exposure Currency',
      getDropdownOptions: () => getCurrencyDrawerOptions(currencies),
      onChange: (newValue, onFormUpdate) => {
        if (!newValue) {
          // Clear the maximum exposure when the currency is cleared
          onFormUpdate('MaximumExposure', '');
        }
      },
    },
    {
      field: 'MaximumExposure',
      type: 'inputNumeric',
      numberInputProps: { integerOnly: true },
      title: 'Maximum Exposure',
      getInputSuffix: form => form.MaximumExposureCurrency ?? '',
      getIsDisabled: form => !form.MaximumExposureCurrency,
    },
  ];
}
